import React from 'react';
import { Card, Form, Button, Spinner, Row, Col } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMotorcycle, faBox, faShoppingCart, faCar,
  faMapMarkerAlt, faRoute,
  faWeight, faUsers, faSearch
} from '@fortawesome/free-solid-svg-icons';
import { estimarTiempo } from '../../utils/tarifaCalculator';
import { VEHICLE_TYPES, SERVICE_TYPES, TRACKING_STATUSES } from '../../../../constants';

const WEIGHT_OPTIONS = [
  { value: 0,  label: 'Sin paquete (viaje)' },
  { value: 2,  label: 'Liviano  — menos de 5 kg' },
  { value: 10, label: 'Mediano  — 5 a 15 kg' },
  { value: 20, label: 'Pesado   — mas de 15 kg' },
];

const CATEGORY_ICONS = { [SERVICE_TYPES.RIDE]: faMotorcycle, [SERVICE_TYPES.DELIVERY]: faBox, [SERVICE_TYPES.ERRAND]: faShoppingCart };

function ServiceRequestForm({
  catalogLoading, catalog, serviceType, handleServiceTypeChange,
  vehicleType, setVehicleType, updateDistancia, pickupPos, dropoffPos,
  pesoKg, selectedCategory, selectedSubcategory, setSelectedSubcategory,
  handlePesoChange, tariffConfig, passengerCount, setPassengerCount,
  handleLocateMe, handleSwapPickupDropoff, pickupAddress, dropoffAddress,
  setPickupAddress, setDropoffAddress, handleAddressSearch,
  nearbyDrivers, referencePoints, setReferencePoints, pendingRefPoint,
  setPendingRefPoint, refPointLabel, setRefPointLabel, refPointMode,
  setRefPointMode, distancia, tarifaResult, handleCalculateDistance,
  handlePickupChange, handleDropoffChange, onOpenCalculator
}) {
  const onlineDrivers = nearbyDrivers.filter(d => d.status === TRACKING_STATUSES.ONLINE);
  const busyDrivers = nearbyDrivers.filter(d => d.status === TRACKING_STATUSES.ON_TRIP);

  const recorridoCosto = Number(
    ((tarifaResult?.breakdown?.distancia || 0) + (tarifaResult?.breakdown?.tiempo || 0)).toFixed(2)
  );

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      {/* Tipo de Servicio */}
      <Card className="border-0 shadow-sm rounded-16 mb-2">
        <Card.Body className="p-3">
          {catalogLoading ? (
            <div className="text-center py-3">
              <Spinner size="sm" className="me-2" />
              <span className="text-muted small">Cargando tipos de servicio...</span>
            </div>
          ) : catalog.length === 0 ? (
            <div className="alert alert-warning py-2 text-center small mb-0">
              No hay tipos de servicio disponibles
            </div>
          ) : (
            <>
              <div className="d-flex gap-2">
                {catalog.map(cat => {
                  const IconComp = CATEGORY_ICONS[cat.slug] || faMotorcycle;
                  return (
                    <Button
                      key={cat.id}
                      variant={serviceType === cat.slug ? 'primary' : 'light'}
                      onClick={() => handleServiceTypeChange(cat.slug)}
                      className="flex-fill rounded-pill py-2 border-0 fw-bold"
                      style={serviceType === cat.slug ? { background: '#0EABB0', color: '#fff' } : {}}
                    >
                      <FontAwesomeIcon icon={IconComp} className="me-1" /> {cat.name}
                    </Button>
                  );
                })}
              </div>

              {/* Selector de Vehículo */}
              <div className="mt-3">
                <Form.Label className="small text-muted fw-bold mb-1">
                  Vehículo preferido
                </Form.Label>
                <div className="d-flex gap-2">
                  <Button
                    variant={vehicleType === VEHICLE_TYPES.MOTO ? 'dark' : 'light'}
                    className="flex-fill rounded-pill py-2 border-0 fw-bold"
                    onClick={() => {
                      setVehicleType(VEHICLE_TYPES.MOTO);
                      updateDistancia(pickupPos, dropoffPos, pesoKg, VEHICLE_TYPES.MOTO);
                    }}
                  >
                    <FontAwesomeIcon icon={faMotorcycle} className="me-1" /> Moto
                  </Button>
                  <Button
                    variant={vehicleType === VEHICLE_TYPES.CARRO ? 'dark' : 'light'}
                    className="flex-fill rounded-pill py-2 border-0 fw-bold"
                    onClick={() => {
                      setVehicleType(VEHICLE_TYPES.CARRO);
                      updateDistancia(pickupPos, dropoffPos, pesoKg, VEHICLE_TYPES.CARRO);
                    }}
                  >
                    <FontAwesomeIcon icon={faCar} className="me-1" /> Carro
                  </Button>
                </div>
              </div>

              {selectedCategory?.subcategories?.length > 0 && (
                <div className="mt-3">
                  <Form.Label className="small text-muted fw-bold mb-1">Subtipo</Form.Label>
                  <div className="d-flex gap-2 flex-wrap">
                    {selectedCategory.subcategories.map(sub => (
                      <Button
                        key={sub.id}
                        size="sm"
                        variant={selectedSubcategory?.id === sub.id ? 'dark' : 'light'}
                        className="rounded-pill border-0 fw-bold"
                        style={{ fontSize: 12 }}
                        onClick={() => setSelectedSubcategory(sub)}
                      >
                        {sub.name} — ${sub.base_price}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Selector de peso — solo para delivery/errand */}
          {serviceType !== SERVICE_TYPES.RIDE && (
            <div className="mt-3">
              <Form.Label className="small text-muted fw-bold mb-1">
                <FontAwesomeIcon icon={faWeight} className="me-1" />
                Peso aproximado del paquete
              </Form.Label>
              <div className="d-flex gap-2 flex-wrap">
                {WEIGHT_OPTIONS.filter(w => w.value !== 0).map(opt => (
                  <Button
                    key={opt.value}
                    size="sm"
                    variant={pesoKg === opt.value ? 'dark' : 'light'}
                    className="rounded-pill border-0 fw-bold"
                    style={{ fontSize: 12 }}
                    onClick={() => handlePesoChange(opt.value)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Surge indicator */}
          {tariffConfig.surge_multiplier > 1 && (
            <div className="mt-2 px-3 py-2 rounded-12"
                 style={{ background: '#E6F8F9', border: '1px solid #FFE0B2' }}>
              <small style={{ color: '#088D91', fontWeight: 700 }}>
                Alta demanda — tarifa x{tariffConfig.surge_multiplier}
              </small>
            </div>
          )}

          {/* Cantidad de pasajeros */}
          <div className="mt-3">
            <Form.Label className="small text-muted fw-bold mb-1">
              <FontAwesomeIcon icon={faUsers} className="me-1" />
              Cantidad de pasajeros
            </Form.Label>
            <div className="d-flex gap-2">
              {[1, 2, 3].map(n => (
                <Button
                  key={n}
                  size="sm"
                  variant={passengerCount === n ? 'dark' : 'light'}
                  className="rounded-pill border-0 fw-bold"
                  style={{ fontSize: 12, minWidth: 44 }}
                  onClick={() => setPassengerCount(n)}
                >
                  {n}
                </Button>
              ))}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Direcciones + Distancia */}
      <Card className="border-0 shadow-sm rounded-16 mb-2">
        <Card.Body className="p-3">
          <Row className="g-2 align-items-center mb-1">
            <Col xs={1} style={{ color: '#0EABB0' }}><FontAwesomeIcon icon={faMapMarkerAlt} /></Col>
            <Col xs={11}>
              <div className="d-flex bg-light rounded-pill p-1 align-items-center">
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="Dirección de recogida"
                  value={pickupAddress}
                  onChange={(e) => setPickupAddress(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddressSearch(pickupAddress, 'pickup'); }}
                  className="border-0 bg-transparent shadow-none"
                  style={{ minWidth: 0 }}
                />
                <Button 
                  size="sm" 
                  variant="white" 
                  className="rounded-circle text-muted border-0 shadow-none bg-transparent"
                  onClick={() => handleAddressSearch(pickupAddress, 'pickup')}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </Button>
              </div>
            </Col>
          </Row>
          <div className="text-center my-1">
            <div style={{ width: 2, height: 14, background: '#ddd', margin: '0 auto' }} />
          </div>
          <Row className="g-2 align-items-center mt-1">
            <Col xs={1} style={{ color: '#121212' }}><FontAwesomeIcon icon={faMapMarkerAlt} /></Col>
            <Col xs={11}>
              <div className="d-flex bg-light rounded-pill p-1 align-items-center">
                <Form.Control
                  size="sm"
                  type="text"
                  placeholder="¿A dónde vamos?"
                  value={dropoffAddress}
                  onChange={(e) => setDropoffAddress(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddressSearch(dropoffAddress, 'dropoff'); }}
                  className="border-0 bg-transparent shadow-none"
                  style={{ minWidth: 0 }}
                />
                <Button 
                  size="sm" 
                  variant="white" 
                  className="rounded-circle text-muted border-0 shadow-none bg-transparent"
                  onClick={() => handleAddressSearch(dropoffAddress, 'dropoff')}
                >
                  <FontAwesomeIcon icon={faSearch} />
                </Button>
              </div>
            </Col>
          </Row>

          {distancia > 0 && tarifaResult && (
            <div className="mt-2 p-2.5 rounded-16" style={{ background: 'rgba(14, 171, 176, 0.08)', border: '1px solid rgba(14, 171, 176, 0.28)' }}>
              <div className="d-flex justify-content-between align-items-center mb-1">
                <span className="fw-bold" style={{ color: '#0EABB0', fontSize: '0.88rem' }}>
                  <FontAwesomeIcon icon={faRoute} className="me-2" />
                  Tarifa Estimada
                </span>
                <span className="badge rounded-pill bg-white px-2 py-1 text-dark border shadow-sm small">
                  {distancia} km • ~{estimarTiempo(distancia)} min
                </span>
              </div>
              
              <div className="d-flex justify-content-between align-items-baseline mt-1">
                <div>
                  <div className="small text-muted">
                    Base ${tarifaResult.breakdown?.base?.toFixed(2) || '0.00'} + Recorrido ${recorridoCosto.toFixed(2)}
                  </div>
                </div>
                <div className="fw-bold" style={{ fontSize: '1.2rem', color: '#0F172A' }}>
                  ${tarifaResult.total} <small className="text-muted" style={{ fontSize: '0.72rem' }}>USD</small>
                </div>
              </div>

              {onOpenCalculator && (
                <div className="mt-1.5 pt-1.5 border-top d-flex justify-content-end">
                  <button
                    type="button"
                    onClick={onOpenCalculator}
                    className="btn btn-link btn-sm p-0 text-decoration-none fw-bold"
                    style={{ color: '#0EABB0', fontSize: '0.78rem' }}
                  >
                    Abrir calculadora detallada →
                  </button>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Conductores Cerca */}
      <div className="d-flex align-items-center gap-3 mb-1 px-1">
        <div className="d-flex align-items-center gap-1">
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#00C853' }} />
          <small className="text-muted">{onlineDrivers.length} disponibles</small>
        </div>
        <div className="d-flex align-items-center gap-1">
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--bry-navy)' }} />
          <small className="text-muted">{busyDrivers.length} ocupados</small>
        </div>
      </div>
    </motion.div>
  );
}

export default ServiceRequestForm;
